import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Plus,
  WifiOff,
  RefreshCw,
  Search,
  Menu,
  Lock,
  Unlock,
  Users,
  BookOpen,
  MapPin,
  FileText,
} from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore.ts';
import { useUIStore } from '../../stores/useUIStore.ts';
import ViewerBanner from '../shared/ViewerBanner.tsx';
import BetalingMisluktBalk from '../shared/BetalingMisluktBalk.tsx';
import { useTranslation } from '../../i18n.ts';
import { ROUTE_TITLES } from '../../utils/routeTitles.ts';

interface AppTopbarProps {
  connectionState: 'online' | 'offline' | 'reconnecting';
  activeAnnouncements: Array<{ id: string; type: string; title: string; message: string }>;
  isViewer: boolean;
  onDismissAnnouncement: (id: string) => void;
  children?: React.ReactNode;
}

const AppTopbar: React.FC<AppTopbarProps> = ({
  connectionState,
  activeAnnouncements,
  isViewer,
  onDismissAnnouncement,
  children,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  // ── Zustand stores ──
  const { currentProject } = useProjectStore();
  const {
    setSidebarOpen,
    setIsCommandPaletteOpen,
    dashWidgetsUnlocked,
    setDashWidgetsUnlocked,
    setDashShowCatalog,
    dashStats,
  } = useUIStore();

  return (
    <>
      <header>
        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <button
            className="hamburger-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('openNavigation')}
          >
            <Menu size={18} />
          </button>
          <span className="mobile-wordmark">
            <span style={{ color: '#F0DEB3' }}>Peck</span>
            <span style={{ color: '#D4AF37' }}>tree</span>
          </span>
        </div>

        {/* Desktop topbar (hidden on tree page — tree has its own toolbar) */}
        <div
          className="topbar no-print"
          style={location.pathname === '/tree' ? { display: 'none' } : undefined}
        >
          <div className="breadcrumb">
            <span>Pecktree</span>
            <span className="breadcrumb-sep">/</span>
            <span>{t(ROUTE_TITLES[location.pathname] || 'dashboard')}</span>
          </div>
          <div
            className="topbar-search"
            onClick={() => setIsCommandPaletteOpen(true)}
            role="button"
            tabIndex={0}
          >
            <Search size={15} />
            <span className="topbar-search-text">{t('search')}...</span>
            <span className="topbar-search-kbd">
              <kbd className="kbd">{navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}</kbd>
              <kbd className="kbd">K</kbd>
            </span>
          </div>
          {currentProject && (
            <div className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-pt-forest dark:text-pt-cream truncate max-w-[40%] pointer-events-none">
              {currentProject.name}
            </div>
          )}
          <div className="topbar-spacer" />
          {location.pathname === '/' && dashStats && (
            <div className="flex items-center gap-3 mr-2">
              {(
                [
                  { key: 'persons', icon: <Users size={13} />, label: t('statPersonsName') },
                  { key: 'sources', icon: <FileText size={13} />, label: t('statSourcesName') },
                  { key: 'places', icon: <MapPin size={13} />, label: t('statPlacesName') },
                  { key: 'stories', icon: <BookOpen size={13} />, label: t('statStoriesName') },
                ] as const
              ).map((s) => (
                <div
                  key={s.key}
                  className="flex items-center gap-1 text-xs text-pt-forest/60 dark:text-white/50"
                  title={s.label}
                >
                  {s.icon}
                  <span className="font-semibold text-pt-forest dark:text-white/70">
                    {(dashStats as any)[s.key] ?? 0}
                  </span>
                </div>
              ))}
            </div>
          )}
          {location.pathname === '/' && (
            <>
              <button
                className={`theme-toggle-btn ${dashWidgetsUnlocked ? 'active' : ''}`}
                onClick={() => setDashWidgetsUnlocked(!useUIStore.getState().dashWidgetsUnlocked)}
                title={dashWidgetsUnlocked ? t('uiWidgetsLock') : t('uiWidgetsUnlock')}
                aria-pressed={dashWidgetsUnlocked}
              >
                {dashWidgetsUnlocked ? <Unlock size={16} /> : <Lock size={16} />}
              </button>
              {dashWidgetsUnlocked && (
                <button
                  className="theme-toggle-btn"
                  onClick={() => setDashShowCatalog(true)}
                  title={t('widgetCatalogTitle')}
                >
                  <Plus size={16} />
                </button>
              )}
            </>
          )}
          {/* Dark mode toggle moved to sidebar footer */}
        </div>
      </header>

      {/* Main content area */}
      <main id="main-content" className="content-area">
        <h1 className="sr-only">{t(ROUTE_TITLES[location.pathname] || 'dashboard')}</h1>
        {/* Connection status banner */}
        {connectionState === 'offline' && (
          <div
            className="shrink-0 flex items-center justify-center gap-3 px-4 py-2.5 bg-pt-ruby-tint text-pt-ruby border-b border-pt-ruby/20 text-sm font-medium"
            role="alert"
          >
            <WifiOff size={16} />
            <span>{t('connectionLost')}</span>
          </div>
        )}
        {connectionState === 'reconnecting' && (
          <div
            className="shrink-0 flex items-center justify-center gap-3 px-4 py-2.5 bg-amber-50 text-amber-800 border-b border-amber-200 text-sm font-medium"
            role="status"
          >
            <RefreshCw size={16} className="animate-spin" />
            <span>{t('reconnecting')}</span>
          </div>
        )}

        {/* Announcement banners from admin */}
        {activeAnnouncements.length > 0 && (
          <div className="shrink-0">
            {activeAnnouncements.map((a) => (
              <div
                key={a.id}
                className={`flex items-center justify-between px-4 py-2 text-sm font-medium ${
                  a.type === 'warning'
                    ? 'bg-amber-50 text-amber-800 border-b border-amber-200'
                    : a.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border-b border-emerald-200'
                      : 'bg-blue-50 text-blue-800 border-b border-blue-200'
                }`}
              >
                <span>
                  <strong>{a.title}</strong> — {a.message}
                </span>
                <button
                  onClick={() => onDismissAnnouncement(a.id)}
                  className="ml-4 opacity-60 hover:opacity-100 text-lg leading-none"
                  aria-label={t('close')}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
        {/* #1853 — mislukte betaling: account tijdelijk op Gratis, één klik naar bijwerken */}
        <BetalingMisluktBalk />
        {/* Viewer banner: shown when user has read-only access */}
        <ViewerBanner isViewer={isViewer} />

        {children}
      </main>
    </>
  );
};

export { ROUTE_TITLES };
export default AppTopbar;
